import { useRef, useState } from "react";
import { Camera, Upload, X, Image as ImageIcon } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../lib/api";

// ENH-023 — Silently downscale + re-encode gallery photos before upload so a
// 12 MB iPhone photo becomes ~1.5 MB. Non-image files (defensive; the input's
// accept string only allows images anyway) and canvas failures fall back to
// the original file untouched.
async function compressImage(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const MAX_SIDE = 1600;
      let { width, height } = img;
      if (width > MAX_SIDE || height > MAX_SIDE) {
        if (width >= height) {
          height = Math.round((height / width) * MAX_SIDE);
          width = MAX_SIDE;
        } else {
          width = Math.round((width / height) * MAX_SIDE);
          height = MAX_SIDE;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) return resolve(file);          // toBlob failed → send raw
          resolve(new File([blob], file.name, { type: "image/jpeg" }));
        },
        "image/jpeg",
        0.85,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);                                // decode failed → send raw
    };
    img.src = url;
  });
}

/**
 * Reusable photo uploader.
 *
 * Props:
 *  - value: relative URL (e.g. /api/uploads/abc.jpg) or null
 *  - onChange(url|null)
 *  - purpose: "logo" | "horse" | "stable_banner" | "general"
 *  - aspect: "square" | "landscape" | "banner"
 *  - hint: helper text describing recommended size + max
 *  - testid prefix
 */
export default function PhotoUpload({
  value,
  onChange,
  purpose = "general",
  aspect = "landscape",
  hint = "JPEG / PNG / WebP",
  testid = "photo-upload",
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const aspectStyle =
    aspect === "square"
      ? { aspectRatio: "1 / 1", maxWidth: 200 }
      : aspect === "banner"
      ? { aspectRatio: "3 / 1" }
      : { aspectRatio: "3 / 2" };

  const fullUrl = value ? (value.startsWith("http") ? value : `${BACKEND_URL}${value}`) : null;

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      fd.append("purpose", purpose);
      const { data } = await api.post("/uploads/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const clear = () => onChange?.(null);

  return (
    <div data-testid={testid}>
      <div
        onClick={pick}
        style={{
          ...aspectStyle,
          background: "rgba(212,169,106,0.08)",
          border: "1px dashed rgba(212,169,106,0.5)",
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
        }}
        data-testid={`${testid}-target`}
      >
        {fullUrl ? (
          <img
            src={fullUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            data-testid={`${testid}-preview`}
          />
        ) : (
          <div className="flex flex-col items-center gap-2" style={{ color: "#8C9E8A" }}>
            <ImageIcon size={28} />
            <div className="text-xs">Tap to upload</div>
          </div>
        )}

        {fullUrl && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); clear(); }}
            className="se-btn se-btn-ghost"
            style={{ position: "absolute", top: 8, right: 8, padding: 6, background: "rgba(253,250,246,0.95)" }}
            data-testid={`${testid}-clear`}
            aria-label="Remove photo"
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between mt-2 gap-2">
        <p className="text-xs" style={{ color: "#8C9E8A" }} data-testid={`${testid}-hint`}>{hint}</p>
        <div className="flex gap-2">
          <button type="button" onClick={pick} className="se-btn se-btn-ghost" style={{ padding: "6px 10px", fontSize: 12 }} data-testid={`${testid}-pick`}>
            <Upload size={12} /> {uploading ? "Uploading…" : (fullUrl ? "Change" : "Choose file")}
          </button>
          <button type="button" onClick={pick} className="se-btn se-btn-ghost" style={{ padding: "6px 10px", fontSize: 12 }} data-testid={`${testid}-camera`} title="Take photo (mobile)">
            <Camera size={12} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-2 text-xs px-2 py-1 rounded" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid={`${testid}-error`}>
          {error}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFile}
        style={{ display: "none" }}
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
